import React from 'react';
import styled from 'styled-components';

import { buildListingFullAddress, formatCurrency } from 'utils';
import { IContactListing } from '@bb/contacts';
import { AuthContext, ListingLabelContext } from 'contexts';
import { AccessType } from '@bb/domain/dist/types/AppointmentConfig';
import { Listing } from 'api/ListingController/getListing';
import { Flex, Icon } from '@bb/elements-web';
import { Button } from 'antd';
import Divider from '../../Divider';
import theme from '../../theme';
import { Text } from '../../Typography';

import AgentProfile from '../../AgentProfile/AgentProfile';
import ListingImage from '../../ListingImage/ListingImage';

type SimpleListingListItemProps = {
  listing: IContactListing;
  type: 'simple';
};

type FullListing = Pick<
  Listing,
  | '_id'
  | 'line1'
  | 'line2'
  | 'thumbnail'
  | 'city'
  | 'province'
  | 'list_price'
  | 'availability'
  | 'status'
  | 'fullReps'
> & {
  visibility?: Listing['visibility'];
  access?: AccessType;
  // eslint-disable-next-line camelcase
  access_instructions?: any;
};

type FullListingListItemProps = {
  listing: FullListing;
  type?: 'full' | 'team';
};

type Props = (SimpleListingListItemProps | FullListingListItemProps) & {
  inDashboard?: boolean;
};
const isFullListing = (
  listing: IContactListing | FullListingListItemProps['listing'],
): listing is FullListingListItemProps['listing'] => 'subtype' in listing;

const isSimpleListing = (
  listing: IContactListing | FullListingListItemProps['listing'],
): listing is IContactListing => !('subtype' in listing);

// From api/services/StatusService.availableStatuses()
const AVAILABLE_AVAILABILITIES = [
  'Sold Conditionally',
  'Leased Conditionally',
  'Price Change',
  'Extended',
  'Deal Fell Through',
  'New',
  'Show For Backups',
];

const getAccessString = (access: AccessType, accessInstructions: any) => {
  let string = '';

  if (access === 'MasterLock') {
    string = `${access} ${accessInstructions.deviceId}`;
  } else if (access === 'SentriLockV2') {
    string = `SentriLock ${accessInstructions.lbsn}`;
  } else if (access === 'Supra Lockbox') {
    const brand =
      accessInstructions.productCode === 'iBox' ? 'iBox BT LE' : 'Invoy';
    string = `${brand} ${accessInstructions.serialNumber}`;
  } else if (access === 'Lock Box') {
    string = 'Combination Lock Box';
  } else {
    string = access;
  }

  return string;
};

const ListingListItem: React.FC<Props> = props => {
  const { type = 'full', listing, inDashboard } = props;
  const { getLabel, getAvailability } = React.useContext(ListingLabelContext);
  const { auth } = React.useContext(AuthContext);
  const shouldShowConfigLink = React.useMemo(() => {
    if (inDashboard) {
      if (type === 'full' && isFullListing(listing)) {
        return listing.fullReps.some(u => u.id === auth.id);
      }
      return true;
    }

    return false;
  }, [auth, type, inDashboard, listing]);

  if ((type === 'full' || type === 'team') && isFullListing(listing)) {
    const listingAddress = buildListingFullAddress(
      listing.line1,
      listing.line2,
    );

    return (
      <HoverArea>
        <StyledInfoContainer>
          <Container>
            <ImageContainer>
              <ListingImage imageSrc={listing.thumbnail} />
            </ImageContainer>
            <TextContainer>
              <Text ellipsis>
                <Text strong>{listingAddress}</Text> {listing.city},{' '}
                {listing.province}
              </Text>
              <Text ellipsis>
                {formatCurrency(listing.list_price)}{' '}
                <TextDivider type="vertical" />{' '}
                {getAvailability(listing.availability)}{' '}
                <TextDivider type="vertical" />{' '}
                {['pending', 'private'].includes(listing.visibility || '')
                  ? 'DRAFT'
                  : getLabel(listing.status)}
              </Text>
            </TextContainer>
          </Container>
          {shouldShowConfigLink && (
            <StyledConfigButton
              href={`#/listing/${listing._id}/appointments/config`}
              data-cy="dashboard-showing-settings-button"
            >
              <Icon icon="settingFilled" color="black" />
            </StyledConfigButton>
          )}
        </StyledInfoContainer>

        {type === 'full' &&
          (listing.access ||
            AVAILABLE_AVAILABILITIES.includes(listing.availability)) && (
            <AccessContainer>
              {listing.access && (
                <Text>
                  {getAccessString(listing.access, listing.access_instructions)}
                </Text>
              )}
            </AccessContainer>
          )}

        {type === 'team' && (
          <AgentProfileContainer>
            {listing.fullReps!.map((rep, idx) => (
              <AgentProfile key={idx} user={rep} />
            ))}
          </AgentProfileContainer>
        )}
      </HoverArea>
    );
  }

  if (type === 'simple' && isSimpleListing(listing)) {
    const { address } = listing;

    const listingAddress = buildListingFullAddress(
      address.line1,
      address.line2,
    );

    const subText = `${address.city ? `${address.city}, ` : ''}${
      address.province ? `${address.province} ` : ''
    }${address.postalCode}`.trim();

    return (
      <HoverArea style={{ flex: 1 }}>
        <Container style={{ paddingLeft: 0 }}>
          <ImageContainer style={{ paddingLeft: 0 }}>
            <ListingImage imageSrc={listing.thumbnail} />
          </ImageContainer>
          <TextContainer>
            <MainText>{listingAddress}</MainText>
            <SubText>{subText}</SubText>
          </TextContainer>
        </Container>
      </HoverArea>
    );
  }

  return null;
};

export default ListingListItem;

const MainText = styled(Text)`
  color: ${theme.gray8};
`;

const SubText = styled(Text)`
  font-size: 12px;
  line-height: 20px;
  color: ${theme.gray7};
`;

const HoverArea = styled.div`
  &:hover {
    background-color: ${theme.gray2};
  }
`;

const StyledInfoContainer = styled(Flex)`
  align-items: center;
`;
const Container = styled.div`
  max-width: 100%;
  height: 60px;
  display: flex;
  padding: 4px;
  width: calc(100% - 28px);
`;

const StyledConfigButton = styled(Button)`
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid ${theme.grayBase};
  border-radius: 4px;
  &.ant-btn {
    line-height: 23px;
  }
  &:hover {
    border-color: ${theme.black};
  }
`;

const ImageContainer = styled.div`
  padding: 4px 8px;
`;

const TextContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-flow: column;
  justify-content: center;

  font-size: 12px;
  line-height: 20px;
  overflow: hidden;
`;

const AgentProfileContainer = styled.div`
  padding-left: 60px;
  padding-bottom: 12px;
  margin-top: -8px;
  margin-bottom: 4px;
`;

const TextDivider = styled(Divider)`
  margin: 0 4px;
`;

const AccessContainer = styled.div`
  padding: 0 0 12px 60px;
  margin-left: 4px;
  line-height: 0.5;
  font-size: 13px;
`;
